import React, { useMemo, useState } from 'react';
import { Alert, Button, Modal, Table, Tag, Typography } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { ContainerDraft } from '../../types/nonstandardOps';
import { useContainerLabelPrinter } from './containerLabels';

interface Props {
  open: boolean;
  /** 终审提交时新建的容器（带生成的编码和 ID） */
  containers: ContainerDraft[];
  /** 顶部提示：终审已提交、生成了多少收费记录和容器 */
  summary: string;
  /** 离开弹窗（返回指令列表） */
  onDone: () => void;
}

/**
 * 提交终审后先提醒打印容器标签：容器和编码是提交那一刻才生成的，提交前打不了。
 * 每个容器一个"打印标签"（先预览再打印）；在预览里点了打印或下载就算"已打印"（只记在这个弹窗里）。
 * 还有没打印的也可以离开（先提醒一句），以后从指令列表"回看"进终审页补打。
 */
const ContainerLabelsAfterSubmit: React.FC<Props> = ({ open, containers, summary, onDone }) => {
  const [printed, setPrinted] = useState<number[]>([]);
  const printer = useContainerLabelPrinter(target => setPrinted(prev => (prev.includes(target.id) ? prev : [...prev, target.id])));
  const rows = useMemo(() => containers.filter(c => c.applied_container_id && c.applied_container_code), [containers]);
  const unprinted = rows.filter(c => !printed.includes(Number(c.applied_container_id))).length;

  const leave = () => {
    if (unprinted === 0) { onDone(); return; }
    Modal.confirm({
      title: `还有 ${unprinted} 个容器没有打印标签，确定返回吗？`,
      content: '终审已经生效，不受影响。以后可以在指令列表点“回看”进入终审页，在容器卡片上补打标签。',
      okText: '确定返回',
      cancelText: '继续打印',
      onOk: onDone,
    });
  };

  return (
    <>
      <Modal
        title="终审已提交，请打印容器标签"
        open={open}
        width={860}
        maskClosable={false}
        keyboard={false}
        onCancel={leave}
        destroyOnClose
        footer={<Button type="primary" className="nonstd-after-submit-done" onClick={leave}>完成，返回指令列表</Button>}
      >
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message={summary}
          description="新建的容器和编码是提交终审时才生成的，请现在打印标签贴到容器上。点“打印标签”会先预览，在预览里打印或下载。"
        />
        <Table
          className="nonstd-after-submit-containers"
          size="small"
          rowKey={c => String(c.applied_container_id)}
          pagination={false}
          dataSource={rows}
          columns={[
            { title: '序号', width: 60, render: (_: any, __: ContainerDraft, index: number) => index + 1 },
            {
              title: '类型',
              width: 140,
              render: (_: any, c: ContainerDraft) => <Tag color={c.kind === 'ioc' ? 'red' : 'blue'}>{c.kind === 'ioc' ? 'IOC 指令产出容器' : 'SC 仓储容器'}</Tag>,
            },
            {
              title: '容器编码',
              render: (_: any, c: ContainerDraft) => <Typography.Text copyable={{ text: c.applied_container_code || '', tooltips: ['复制', '已复制'] }}>{c.applied_container_code}</Typography.Text>,
            },
            { title: '仓位', width: 130, render: (_: any, c: ContainerDraft) => c.location_code || c.location_text || '-' },
            {
              title: '内容',
              width: 150,
              render: (_: any, c: ContainerDraft) => `${(c.items || []).length} 个库存单元，共 ${(c.items || []).reduce((sum, it) => sum + (Number(it.qty) || 0), 0)} 件`,
            },
            {
              title: '标签',
              width: 90,
              render: (_: any, c: ContainerDraft) => (printed.includes(Number(c.applied_container_id))
                ? <Tag color="success" className="nonstd-after-submit-printed">已打印</Tag>
                : <Tag color="warning" className="nonstd-after-submit-unprinted">未打印</Tag>),
            },
            {
              title: '操作',
              width: 120,
              render: (_: any, c: ContainerDraft) => (
                <Button
                  size="small"
                  icon={<PrinterOutlined />}
                  className="nonstd-after-submit-print"
                  loading={printer.loadingId === c.applied_container_id}
                  onClick={() => { void printer.openLabel({ kind: c.kind, id: Number(c.applied_container_id), code: c.applied_container_code || '' }); }}
                >
                  打印标签
                </Button>
              ),
            },
          ]}
        />
      </Modal>
      {printer.modals}
    </>
  );
};

export default ContainerLabelsAfterSubmit;
